import React from 'react'
import parseISO from 'date-fns/parseISO'
import { useTranslation } from 'react-i18next'
import styled, { withTheme } from 'styled-components'

import Action from '../../components/Action'
import Icon from '../../components/Icon'
import { formatValue } from '../../components/sensors/sensor_value_tile/helpers'

import ValveFailureIcon from './ValveFailureIcon'

const Popup = styled.div`
  min-width: min(360px, calc(100vw - 3rem));
  max-width: min(440px, calc(100vw - 3rem));
  max-height: min(420px, calc(100vh - 5rem));
  padding: 0.5rem;
  padding-top: 1rem;
  overflow-y: auto;
  color: ${({ theme }) => theme.colors.text};
`

const ModuleName = styled.h2`
  margin: 0;
  font-size: 1rem;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
`

const RefreshAction = styled(Action)`
  margin-left: 0.5rem;
  color: ${({ theme }) => theme.colors.text};
  outline: none;

  &:focus {
    outline: none;
  }
`

const Section = styled.section`
  & + & {
    margin-top: 0.75rem;
  }
`

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 0.875rem;
`

// Shared by valve and sensor rows so the columns line up across both sections:
// name | value | timestamp
const rowColumns = `
  display: grid;
  grid-template-columns: minmax(0, 1fr) 90px 115px;
  column-gap: 0.75rem;
`

const SectionHeader = styled.div`
  ${rowColumns}
  align-items: baseline;
  margin-bottom: 0.25rem;
`

const TimestampHeader = styled.span`
  grid-column: 3;
  color: ${({ theme }) => theme.colors.gray};
  font-size: 0.75rem;
`

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`

const ListItem = styled.li`
  ${rowColumns}
  align-items: center;
  min-height: 24px;
  font-size: 0.875rem;
`

const Cell = styled.span`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
`

const Name = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Value = styled.span`
  white-space: nowrap;
`

const Timestamp = styled.span`
  grid-column: 3;
  color: ${({ theme }) => theme.colors.gray};
  font-size: 0.75rem;
  white-space: nowrap;
`

const Empty = styled.span`
  color: ${({ theme }) => theme.colors.gray};
  font-size: 0.875rem;
`

const OfflineStatus = styled.p`
  margin: 0 0 0.75rem;
  color: ${({ theme }) => theme.colors.alert};
  font-size: 0.875rem;
  font-weight: bold;
`

const ValveRow = withTheme(({ valve, theme, t }) => (
  <ListItem>
    <Cell>
      <Name>{valve.name}</Name>
      <ValveFailureIcon
        inputMismatch={valve.inputMismatch}
        inputPending={valve.inputPending}
      />
      {valve.hasUnreadError && (
        <Icon
          small
          solid
          name="exclamation-circle"
          border={theme.colors.gray}
          background={theme.colors.background}
          color={theme.colors.alert}
        />
      )}
    </Cell>
    <Cell>
      <Icon
        small
        bordered={false}
        rotate={valve.currentIsSuspended ? 90 : undefined}
        name={valve.currentIsSuspended ? 'manual' : 'sync'}
      />
      <Icon small bordered={false} name={`circle-${valve.currentValue || 0}`} />
      {!!valve.currentPressure && (
        <Icon small bordered={false} name="tint" color={theme.colors.water} />
      )}
    </Cell>
    {valve.currentTimestamp && (
      <Timestamp>
        {t('map:moduleHover.lastUpdated', {
          timestamp: parseISO(valve.currentTimestamp),
        })}
      </Timestamp>
    )}
  </ListItem>
))

const SensorRow = ({ sensorValue, t }) => {
  const value = sensorValue.currentValue || {}
  return (
    <ListItem>
      <Name>{sensorValue.name}</Name>
      <Value>
        {formatValue(value.value)}{' '}
        {t(`attributes:sensorValue:unitOptions:${sensorValue.unit}`)}
      </Value>
      {value.timestamp && (
        <Timestamp>
          {t('map:moduleHover.lastUpdated', {
            timestamp: parseISO(value.timestamp),
          })}
        </Timestamp>
      )}
    </ListItem>
  )
}

const ModuleHoverPopup = ({ module, valves, sensorValues, onRefresh }) => {
  const { t } = useTranslation()

  return (
    <Popup
      aria-label={t('map:moduleHover.ariaLabel', {
        name: module.mapDisplayName || module.name,
      })}
    >
      <Header>
        <ModuleName>{module.mapDisplayName || module.name}</ModuleName>
        <RefreshAction
          aria-label={t('map:moduleHover.refresh')}
          title={t('map:moduleHover.refresh')}
          onClick={(e) => {
            e.stopPropagation()
            onRefresh()
          }}
        >
          <Icon name="sync" />
        </RefreshAction>
      </Header>
      {module.online === false && (
        <OfflineStatus>{t('map:moduleHover.offline')}</OfflineStatus>
      )}
      <Section>
        <SectionHeader>
          <SectionTitle>{t('models:valve_plural')}</SectionTitle>
          <TimestampHeader>
            {t('map:moduleHover.lastUpdatedHeader')}
          </TimestampHeader>
        </SectionHeader>
        {valves.length > 0 ? (
          <List>
            {valves.map((valve) => (
              <ValveRow key={valve.id} valve={valve} t={t} />
            ))}
          </List>
        ) : (
          <Empty>{t('map:moduleHover.noValves')}</Empty>
        )}
      </Section>
      <Section>
        <SectionHeader>
          <SectionTitle>{t('models:sensor_value_plural')}</SectionTitle>
          <TimestampHeader>
            {t('map:moduleHover.lastUpdatedHeader')}
          </TimestampHeader>
        </SectionHeader>
        {sensorValues.length > 0 ? (
          <List>
            {sensorValues.map((sensorValue) => (
              <SensorRow key={sensorValue.id} sensorValue={sensorValue} t={t} />
            ))}
          </List>
        ) : (
          <Empty>{t('map:moduleHover.noSensorValues')}</Empty>
        )}
      </Section>
    </Popup>
  )
}

export default ModuleHoverPopup
